import { IconButton } from '@ads/components-react';
import { SidebarLayout, type SidebarLayoutItem, type SidebarLayoutSection } from '@linka/ui/sidebar-layout';
import { Skeleton } from '@linka/ui/skeleton';
import { Suspense, useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, NavLink, Outlet, type RouteObject, useLocation, useMatches, useNavigate, useParams } from 'react-router';
import { EventDto, EventRole } from '@/api/services/api/generated/data-contracts';
import { manageRoutes } from '@/app/routes/routes.private';
import { cn, formatDate } from '@/lib/utils';
import { Button } from '@/ui/components/arandu/button';
import { Icon, Icons } from '@/ui/components/icon';
import { useEvent } from '@/ui/hooks/use-event';
import { UserActions } from './default-layout';
import EventOverflowMenu from './event-overflow-menu';
import { PageRequireAuthContext, PageRequireAuthContextProps, useEventPermissions } from './require-auth';
import Surface from './surface';

export const ManageLayout = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const { loggedUserRoleInTeamMembers } = useContext<PageRequireAuthContextProps>(PageRequireAuthContext);
  const [isSidebarContentVisible, setIsSidebarContentVisible] = useState(false);
  const { eventId = '' } = useParams();
  const matches = useMatches();
  const isFullBleed = Boolean(matches[matches.length - 1]?.handle?.fullBleed);

  return (
    <SidebarLayout
      fullBleed={isFullBleed}
      collapseSignal={location.pathname}
      onExpandedChange={setIsSidebarContentVisible}
      logoHref={`/manage/${eventId}/dashboard`}
      renderLogoLink={({ href, children, ...props }) => (
        <Link to={href} {...props}>
          {children}
        </Link>
      )}
      expandLabel={t('open-sidebar')}
      collapseLabel={t('close-sidebar')}
      sections={buildSidebarSections(loggedUserRoleInTeamMembers ?? ['ADMINISTRATOR'])}
      renderLink={(item, props) => <NavLink to={item.href ?? ''} {...props} />}
      sidebarContent={
        <Suspense fallback={<SidebarCurrentEventSkeleton />}>
          <SidebarCurrentEvent isSidebarContentVisible={isSidebarContentVisible} />
        </Suspense>
      }
      sidebarFooter={
        <Link
          className='flex items-center group-data-[expanded=false]:justify-center gap-micro text-ctx-content-base'
          to='/events'
        >
          <Icon icon='home-icon' className='size-xxs' />
          <span className='group-data-[expanded=false]:sr-only ts-subtitle-xs'>{t('my-events')}</span>
        </Link>
      }
      header={<UserActions />}
    >
      {isFullBleed ? (
        <div className='h-full relative overflow-hidden'>
          <Outlet />
        </div>
      ) : (
        <Surface className='h-full relative overflow-hidden'>
          <Outlet />
        </Surface>
      )}
    </SidebarLayout>
  );
};

const SidebarCurrentEventSkeleton = () => (
  <article className='flex flex-col gap-quark p-mini border-hairline border-ctx-layout-border rounded-sm'>
    <header className='flex items-center justify-between'>
      <Skeleton className='h-[1.25rem] w-[5.625rem] rounded-md' />
      <Skeleton className='h-[2rem] w-[2rem] rounded-sm' />
    </header>
    <div>
      <Skeleton className='h-[1.25rem] w-2/3 mb-nano' />
      <Skeleton className='h-[0.875rem] w-1/2 mb-quark' />
      <Skeleton className='h-[0.875rem] w-1/3' />
    </div>
    <Skeleton className='h-[2.25rem] w-full rounded-md mt-quark' />
  </article>
);

interface SidebarCurrentEventProps {
  isSidebarContentVisible: boolean;
}

const SidebarCurrentEvent = ({ isSidebarContentVisible }: SidebarCurrentEventProps) => {
  const { eventId = '' } = useParams();
  const { t } = useTranslation();
  const { data } = useEvent(eventId);
  const { isAdmin } = useEventPermissions();
  const nav = useNavigate();

  return (
    <article
      className={`flex flex-col gap-quark p-mini ${!isSidebarContentVisible ? 'items-center' : ''} border-hairline border-ctx-layout-border rounded-sm w-full`}
    >
      <header className='flex items-center justify-between'>
        <div
          className={cn('flex items-center gap-nano', {
            hidden: !isSidebarContentVisible,
          })}
        >
          <div className={`size-[0.375rem] rounded-pill animate-pulse ${eventStatus[data.status]}`} />
          <span className='ts-paragraph-xxs text-base-gray-500'>{t(`event-status.${data.status}`)}</span>
        </div>

        <EventOverflowMenu
          eventStatus={data.status}
          eventId={eventId}
          options={{
            viewEvent: data.status !== 'DRAFT',
            delete: data.status === 'DRAFT' && isAdmin,
          }}
        />
      </header>

      <div className={cn({ hidden: !isSidebarContentVisible })}>
        <p className='ts-heading-xs text-ctx-content-title mb-nano'>{data.name}</p>
        <p className='ts-caption-xxs text-ctx-content-base mb-quark'>
          {formatDate({
            dateFormat: 'd MMM. yyyy, HH:mm',
            date: data.startDate,
          })}
        </p>
        <p className='ts-caption-xxs text-ctx-content-base'>{t(`event-format.${data.format}`)}</p>
      </div>

      {isAdmin &&
        (!isSidebarContentVisible ? (
          <IconButton
            className='!rounded-pill border-ctx-interactive-primary border-hairline !size-xs'
            ariaLabel={t('edit')}
            icon={() => <Icon icon='square-pen' className='size-min' />}
            hierarchy='secondary'
            onClick={() => nav(`/events/${eventId}/details`)}
          />
        ) : (
          <Button
            trailingIcon={() => <Icon icon='square-pen' className='size-mini' />}
            hierarchy='secondary'
            fullWidth
            onClick={() => nav(`/events/${eventId}/details`)}
          >
            {t('edit')}
          </Button>
        ))}
    </article>
  );
};

const eventStatus: Record<EventDto['status'], string> = {
  PUBLISHED: 'bg-base-success-700',
  PRIVATE: 'bg-base-warning-700',
  DRAFT: 'bg-base-gray-700',
};

const buildSidebarSections = (roles: EventRole[]): SidebarLayoutSection[] => {
  const routes = roles.flatMap((role) => sidebarRoutes()[role] ?? []);
  const uniqueRoutes = routes.filter((route, index, arr) => index === arr.findIndex((r) => r.path === route.path));

  const items = uniqueRoutes
    .map((route): SidebarLayoutItem | null => {
      if (!route.path) return null;

      const icon = route.handle?.icon as Icons;
      const isMultiple =
        route.children && route.children.length > 1 && !route.children.some((subitem) => subitem.index);

      if (isMultiple) {
        const children = (route.children ?? [])
          .filter((child) => child.path && canAccessCollapsibleSubitem(String(child.path), roles))
          .map(
            (child): SidebarLayoutItem => ({
              id: `${route.path}/${child.path}`,
              href: `${route.path}/${child.path}`,
              label: child.handle?.label,
            }),
          );

        return { id: route.path, label: route.handle.label, icon: icon && <Icon icon={icon} />, children };
      }

      return {
        id: route.path,
        href: route.path,
        label: route.handle.label,
        icon: icon && <Icon icon={icon} />,
      };
    })
    .filter((item): item is SidebarLayoutItem => item !== null);

  return [{ id: 'manage', items }];
};

const sidebarRoutes = () => {
  const checkInOperatorRoutes = manageRoutes.filter((route) => ['participants'].includes(`${route.path}`));
  const belongingsOperatorRoutes = manageRoutes.filter((route) => {
    if (route.children) return route.children.some((child) => child.path === 'belongings-control');
    return false;
  });
  const routes: Record<EventRole, RouteObject[]> = {
    BELONGINGS_OPERATOR: belongingsOperatorRoutes,
    CHECK_IN_OPERATOR: checkInOperatorRoutes,
    // Operador de caixa trabalha no app do terminal, não tem tela no organizer
    POS_OPERATOR: [],
    ADMINISTRATOR: manageRoutes,
    OWNER: manageRoutes,
  };
  return routes;
};

const canAccessCollapsibleSubitem = (subitemPath: string, roles: EventRole[]) => {
  if (roles.includes('ADMINISTRATOR') || roles.includes('OWNER')) return true;
  if (roles.includes('BELONGINGS_OPERATOR') && collapsibleRoutes.BELONGINGS_OPERATOR.includes(subitemPath)) return true;
  if (roles.includes('CHECK_IN_OPERATOR') && collapsibleRoutes.CHECK_IN_OPERATOR.includes(subitemPath)) return true;
  return false;
};

const collapsibleRoutes: Record<EventRole, string[]> = {
  BELONGINGS_OPERATOR: ['belongings-control'],
  CHECK_IN_OPERATOR: ['list-participants', 'import-participants', 'label-configuration'],
  POS_OPERATOR: [],
  OWNER: ['*'],
  ADMINISTRATOR: ['*'],
};
